import { useState, useEffect, useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";
import "react-quill/dist/quill.snow.css";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import { useNavigate } from "react-router-dom";
import "./NewPrivacyPolicy.css";
import { Checkbox } from "../Checkbox/Checkbox";
import { InputForm } from "../InputForm/InputForm";
import { Button, Container } from "react-bootstrap";
import Select from "react-select";
import { PolicyType } from "../PrivacyPolicy/PrivacyPolicType";
import { Notification } from "../Notification/Notification";
import { Heading, useAuthenticator } from "@aws-amplify/ui-react";
import { selectStyles } from "../EditPolicy/EditPolicyTypes";
import { BackButton } from "../_shared/Buttons/BackButton";

type OptionType = {
  value: string;
  label: string;
};

const options: OptionType[] = [
  { value: "ti", label: "Travel Insurance" },
  { value: "tmc", label: "Travel Money Card" },
  { value: "app", label: "Travel App" },
];

export const NewPrivacyPolicy = () => {
  const { signOut } = useAuthenticator();
  const [policy, setPolicy] = useState({
    id: "",
    policyType: "",
    header: "",
    body: "",
    isActive: false,
  } as PolicyType);
  const [toastTitle, setToastTitle] = useState("");
  const [toastDescription, setToastDescription] = useState("");
  const [showToast, setShowToast] = useState(false);

  const navigate = useNavigate();

  const handleChangeText = (value: string, name: string) => {
    setPolicy((policy) => ({
      ...policy,
      [name]: value,
    }));
  };

  const handleDescription = (event) => {
    const newBody = event.target.value;
    setPolicy((policy) => ({
      ...policy,
      body: newBody,
    }));
  };

  const handleNotificationClose = () => {
    setShowToast(false);
    goBack();
  };

  const handleSelectChange = (selected) => {
    setPolicy((policy) => ({
      ...policy,
      policyType: selected.value,
    }));
  };

  const goBack = () => {
    navigate(-1);
  };

  const fetchPolicies = useCallback(async () => {
    try {
      const policies = await API.graphql(graphqlOperation(queries.getPolicy));
      console.log("DISPLAY THE POLICIES:", JSON.stringify(policies));
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchPolicies(); // Fetch policies when component mounts
  }, [fetchPolicies]);

  const submitPolicy = async () => {
    try {
      policy.id = uuidv4();
      console.log("Policy we are sending:", JSON.stringify(policy));
      await API.graphql(
        graphqlOperation(mutations.createPolicy, {
          policy,
        })
      );
      setToastTitle("Success!");
      setToastDescription("You successfully created a policy");
      setShowToast(true);
    } catch (e) {
      console.error(e);
      signOut();
    }
  };

  const handleSubmitPolicy = async () => {
    if (policy.policyType && policy.body && policy.header) {
      submitPolicy();
    } else {
      setShowToast(true);
      setToastTitle("Error!");
      setToastDescription(
        "All fields are required, please fill all fields to continue"
      );
    }
  };

  const toastVariant = toastTitle === "Error!" ? "danger" : "success";

  const handleCheckValue = (event: React.MouseEvent<HTMLInputElement>) => {
    const currentTarget = event.target as HTMLInputElement;
    const checked = currentTarget.checked;
    const name = currentTarget.name;
    setPolicy((policy) => ({
      ...policy,
      [name]: checked,
    }));
  };

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/policy");
  };

  return (
    <div>
      <Container fluid as={"div"} className="screenContainer">
        <div className="pushCard">
          <BackButton onClick={onClick} label={"Back to policies"} />
          <Heading level={2} className="screen-title">
            Add New Policy
          </Heading>
          <div className="formContainer">
            <div className="formItem">
              <InputForm
                name="header"
                ariaLabel="Title"
                size="sm"
                required={true}
                inLineLabel
                onChange={handleChangeText}
                labelText="Policy Header"
                controlId="formMessages"
                defaultValue={policy?.header}
              />
            </div>
            <div className="formItem field-row">
              <div className="description-title">Select Policy Type</div>
              <Select
                defaultValue={{
                  value: "",
                  label: "Please select a policy type",
                }}
                styles={selectStyles}
                options={options}
                onChange={handleSelectChange}
              />
            </div>
            <div className="formItem field-row">
              <div className="description-title desc">Policy Body</div>
              <textarea
                value={policy.body}
                className="description"
                onChange={handleDescription}
                defaultValue={policy?.body}
              />
            </div>
            <div className="formItem">
              <span className="checkbox-item">
                <Checkbox
                  label="Is Active"
                  name="isActive"
                  handleCheck={handleCheckValue}
                  defaultChecked={false}
                  className="action-checkbox"
                />
              </span>
            </div>
            <div className="formItem formActionsRight">
              <Button
                type="button"
                variant="primary"
                className="btn btn-primary"
                onClick={() => handleSubmitPolicy()}
              >
                Save
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                className="btn btn-outline-secondary"
                onClick={goBack}
              >
                Cancel
              </Button>
            </div>
          </div>
          <div className="notificationContainer">
            <Notification
              show={showToast}
              timer={4000}
              position="top-center"
              title={toastTitle}
              description={toastDescription}
              autohide={true}
              handleClose={handleNotificationClose}
              variant={toastVariant}
            />
          </div>
        </div>
      </Container>
    </div>
  );
};
